import type { Operaciones } from '../../api/operaciones';
import type { CanalPedido, EstadoPedido, Evento, FiltrosPedidos, FormaPagoPedido, OperacionesPedidos, Pedido } from '../../api/ops/pedidos';
import { ErrorApi } from '../../api/problema';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import { diaYHora } from '../../logica/fechasCortas';
import { horaEn } from '../eventos/reglas';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos y reglas de «Entradas» (Pedidos 10.6 e Informes 10.8) y de «Taquilla» (10.7), tal cual de la exportación
 * (`diseno/eventos-entradas/10.6-10.8-pedidos-taquilla-informes.html`) salvo donde una decisión la corrige:
 * - G121: sin cifras por filtro ni totales de lista; «Anterior / Siguiente».
 * - G290: cada gráfico con su tabla; sin minigráficos ni variaciones que la API no da («frente al…» fuera).
 * - G386: `?pedido=` abre el pedido (destino de los avisos de reembolso).
 * - G508: corregir el nombre deja el pase en una versión nueva con el mismo QR.
 * - G510, G540, G572: el canal (Web, Widget, Asistente, Panel, Taquilla, Integración), mapa común de canales.
 * - G511: un pedido cancelado porque el cliente pidió borrar sus datos sale como «Cliente suprimido», sin reembolso.
 * - G786, G926: quién vendió y con qué tablet (`created_by_name`, `device_name`, guardados en el pedido); el color de cada tipo en la taquilla.
 * - Pagos, pregunta 4 (abierta): al reembolsar, por defecto **sin** gastos de gestión.
 */
export const PERMISOS = {
    pedidosVer: 'entradas.pedidos.ver',
    gestionar: 'entradas.pedidos.gestionar',
    reembolsar: 'entradas.pedidos.reembolsar',
    informes: 'entradas.informes.ver',
    eventosVer: 'entradas.eventos.ver',
    vender: 'entradas.taquilla.vender',
    reenviar: 'pases.pases.reenviar',
    clientes: 'clientes.fichas.ver',
    // Nombres del equipo en «Últimas ventas» de la taquilla (10.6 ya no los pide: G926).
    miembros: 'equipo.miembros.ver',
} as const;

/**
 * Las operaciones de esta pantalla. `operacionesPedidos` se junta en `crearOperaciones` (`api/operaciones.ts`); hasta
 * entonces, y en las pruebas, llegan por aquí con su tipo.
 */
export function opsEntradas(ops: Operaciones): Operaciones & OperacionesPedidos {
    return ops;
}

const ayuda = (filas: readonly (readonly [NombreIcono, string, string])[]): TarjetaAyuda[] => filas.map(([icono, titulo, texto]) => ({ icono, titulo, texto }));

export const AYUDA = {
    pedidos: ayuda([
        ['receipt', 'Pedidos', 'Cada compra de entradas, venga de donde venga: web, widget, asistente, panel o taquilla. Busca por el localizador de 8 caracteres.'],
        ['rotate-ccw', 'Cancelar y reembolsar', 'Anula los pases, libera las plazas y devuelve el dinero a su tarjeta. En taquilla, el dinero lo devuelves tú.'],
        ['send', 'Reenviar', 'Las entradas van al contacto de su ficha, por su canal preferido y con respaldo por email.'],
    ]),
    taquilla: ayuda([
        ['ticket', 'Vender en la puerta', 'Elige las entradas, cobra con efectivo o con vuestro datáfono y elige cómo las recibe. Reservaria solo anota la forma de pago.'],
        ['wifi-off', 'Sin conexión', 'Con el paquete de puerta preparado, sigues vendiendo del cupo de taquilla guardado en el dispositivo. Se confirma solo al volver la conexión.'],
        ['undo-2', '¿Te has equivocado?', 'Tienes 5 segundos para deshacer la venta. Después, cancélala desde Pedidos.'],
    ]),
    informes: ayuda([
        ['chart-column', 'Informes', 'Ventas por tipo y canal, aforo, llegadas y listas de cada evento.'],
        ['door-open', 'En vivo', 'Durante el evento, «dentro ahora» se actualiza con cada pase que entra por la puerta.'],
        ['table', 'Tabla', 'Cada gráfico tiene su tabla con las mismas cifras.'],
        ['shield-check', 'Solo cifras', 'Sin datos de los compradores. Los ingresos no incluyen los gastos de gestión.'],
    ]),
    pedido: ayuda([
        ['receipt', 'El pedido', 'Qué compró, cuándo y por dónde. Su contacto está en su ficha de cliente: aquí no se ve.'],
        ['ticket', 'Entradas', 'Una por asistente, con su pase y su llegada. En los eventos nominativos puedes corregir el nombre: el QR no cambia.'],
    ]),
};

/** Mapa común de canales (G510, G540, G572). */
export const CANAL: Readonly<Record<CanalPedido, readonly [string, NombreIcono]>> = {
    web: ['Web', 'globe'],
    widget: ['Widget', 'code'],
    assistant: ['Asistente', 'bot'],
    panel: ['Panel', 'layout-grid'],
    box_office: ['Taquilla', 'ticket'],
    api: ['Integración', 'code'],
};

export const ESTADO_PEDIDO: Readonly<Record<EstadoPedido, readonly [string, EstadoChip]>> = {
    pending_payment: ['Pendiente de pago', 'pending'],
    confirmed: ['Confirmado', 'confirmed'],
    expired: ['Caducado', 'problem'],
    cancelled: ['Cancelado', 'problem'],
    refunded: ['Reembolsado', 'neutral'],
};

export const FORMA_PAGO: Readonly<Record<FormaPagoPedido, readonly [string, NombreIcono]>> = {
    online: ['Tarjeta online', 'credit-card'],
    cash: ['Efectivo en taquilla', 'banknote'],
    card_terminal: ['Datáfono en taquilla', 'credit-card'],
    free: ['Gratis', 'ticket'],
};

/** Filtros de la lista (sin cifras, G121). «Reembolso fallido» solo para quien puede reintentarlo. */
export interface FiltroPedidos {
    clave: 'todos' | 'confirmados' | 'sin-pagar' | 'cancelados' | 'fallidos';
    etiqueta: string;
    filtros: Pick<FiltrosPedidos, 'status' | 'refund_status'>;
}

export function filtrosDePedidos(puedeReembolsar: boolean): FiltroPedidos[] {
    const todos: FiltroPedidos[] = [
        { clave: 'todos', etiqueta: 'Todos', filtros: {} },
        { clave: 'confirmados', etiqueta: 'Confirmados', filtros: { status: 'confirmed' } },
        { clave: 'sin-pagar', etiqueta: 'Sin pagar', filtros: { status: 'pending_payment' } },
        // «Cancelados» suma los cancelados y los ya reembolsados en una sola llamada (G827); los caducados, en «Todos».
        { clave: 'cancelados', etiqueta: 'Cancelados', filtros: { status: ['cancelled', 'refunded'] } },
        { clave: 'fallidos', etiqueta: 'Reembolso fallido', filtros: { refund_status: 'failed' } },
    ];
    return puedeReembolsar ? todos : todos.filter((f) => f.clave !== 'fallidos');
}

/** El localizador: 8 caracteres sin I, L, O ni U (`OrderReference`). Devuelve el normalizado o null si no vale. */
export function localizador(texto: string): string | null {
    const t = texto.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
    return /^[0-9A-HJKMNP-TV-Z]{8}$/.test(t) ? t : null;
}

/** Quién compró, sin su contacto (G511: «Cliente suprimido»; venta en la puerta sin datos: «Venta en taquilla»). */
export function nombreComprador(p: Pick<Pedido, 'customer_id' | 'cancelled_by' | 'channel'>, nombres: Readonly<Record<string, string>>): string {
    if (p.cancelled_by === 'customer_privacy') {
        return 'Cliente suprimido';
    }
    if (p.customer_id == null) {
        return p.channel === 'box_office' ? 'Venta en taquilla' : 'Sin cliente';
    }
    const n = nombres[p.customer_id];
    return n === undefined || n === '' ? 'Cliente' : n;
}

/** Iniciales para la baldosa del comprador («Pablo Ortega» → «PO»). */
export function iniciales(nombre: string): string {
    const partes = nombre.trim().split(/\s+/).filter((x) => x !== '');
    return ((partes[0]?.charAt(0) ?? '') + (partes[1]?.charAt(0) ?? '')).toLocaleUpperCase('es') || '·';
}

/** «2 × General · 1 × Jubilado». */
export function lineasDe(p: Pick<Pedido, 'lines'>): string {
    return p.lines.map((l) => `${String(l.quantity)} × ${l.name ?? 'Entrada'}`).join(' · ');
}

/** Entradas del pedido (la suma de las líneas: es una cuenta de entradas, no de dinero). */
export function entradasDe(p: Pick<Pedido, 'lines'>): number {
    return p.lines.reduce((n, l) => n + l.quantity, 0);
}

/** Minutos que quedan para pagar (G39), redondeando hacia arriba; 0 si ya pasó; null si no hay plazo. */
export function minutosParaPagar(p: Pick<Pedido, 'payment_expires_at'>, ahora: Date): number | null {
    if (p.payment_expires_at == null) {
        return null;
    }
    const ms = new Date(p.payment_expires_at).getTime() - ahora.getTime();
    return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 60_000));
}

/** La línea de debajo del comprador en la lista: «D9H2M6WQ · Hoy, 21:57» o «P3Z7A5NK · caduca en 18 min». */
export function lineaDelPedido(p: Pedido, ahora: Date): string {
    const min = p.status === 'pending_payment' ? minutosParaPagar(p, ahora) : null;
    return `${p.reference} · ${min === null ? diaYHora(p.created_at, ahora) : `caduca en ${String(min)} min`}`;
}

/** El chip de la fila: solo lo que pide mirar (pendiente, fallido, cancelado…); un confirmado normal va sin chip. */
export function chipDeFila(p: Pick<Pedido, 'status' | 'refund_status'>): readonly [string, EstadoChip] | null {
    if (p.refund_status === 'failed') {
        return ['Reembolso fallido', 'problem'];
    }
    if (p.refund_status === 'requested') {
        return ['Reembolso en curso', 'pending'];
    }
    return p.status === 'confirmed' ? null : ESTADO_PEDIDO[p.status];
}

/** Quién canceló el pedido (`cancelled_by`). */
export function textoCancelado(p: Pick<Pedido, 'cancelled_by'>): string {
    switch (p.cancelled_by) {
        case 'business':
            return 'Lo canceló el negocio';
        case 'event_cancellation':
            return 'Se canceló el evento';
        case 'date_change':
            return 'Lo canceló el comprador tras el cambio de fecha';
        case 'customer_privacy':
            return 'El cliente pidió borrar sus datos: sin reembolso';
        case 'system':
            return 'Lo canceló el sistema';
        default:
            return 'Cancelado';
    }
}

/** El pedido se pagó en la puerta (efectivo o datáfono): el dinero se devuelve fuera de Reservaria. */
export function pagoEnElLocal(p: Pick<Pedido, 'payment_method'>): boolean {
    return p.payment_method === 'cash' || p.payment_method === 'card_terminal';
}

export type FormaReembolso = 'full' | 'without_booking_fee' | 'none_external';

/**
 * Cómo se puede devolver un pedido. Online: «Sin gastos de gestión» (por defecto mientras la pregunta 4 de Pagos siga
 * abierta, como `OrderCancel`) y «Todo»; sin gastos de gestión, solo «Todo». En taquilla, solo «En el local».
 */
export function formasDeReembolso(p: Pick<Pedido, 'payment_method' | 'booking_fee'>): FormaReembolso[] {
    if (pagoEnElLocal(p)) {
        return ['none_external'];
    }
    return p.booking_fee.amount > 0 ? ['without_booking_fee', 'full'] : ['full'];
}

/** Lo que se devuelve con cada forma: cifras de la API (`subtotal`, `total`), nunca calculadas. */
export function importeDevuelto(p: Pick<Pedido, 'subtotal' | 'total'>, forma: FormaReembolso): Pedido['total'] | null {
    if (forma === 'none_external') {
        return null;
    }
    return forma === 'full' ? p.total : p.subtotal;
}

/** Texto de error de la API para un aviso breve. */
export function textoDeError(e: unknown, porDefecto: string): string {
    if (e instanceof ErrorApi) {
        if (e.estado === 412) {
            return 'El pedido ha cambiado mientras lo mirabas. Ya tienes la versión nueva: revisa y vuelve a intentarlo.';
        }
        if (e.estado === 429) {
            return 'Has llegado al límite de 5 reenvíos por entrada en una hora. Prueba más tarde.';
        }
        if (e.estado === 403) {
            return 'Tu rol no puede hacer esto.';
        }
    }
    return porDefecto;
}

/** «Hoy, 21:30» del evento, o vacío. */
export function cuandoEvento(e: Pick<Evento, 'starts_at'> | undefined, ahora: Date = new Date()): string {
    return e === undefined ? '' : diaYHora(e.starts_at, ahora);
}

/** El color de la baldosa de un tipo (`#RRGGBB`, G786); otro valor o nulo, el color neutro. */
export function colorDeTipo(color: string | null | undefined): string | null {
    return typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : null;
}

/** Aviso de arriba de la ficha según el estado del pedido y de su reembolso. */
/**
 * G969: un pedido pendiente «se anula solo si no se paga antes de las HH:MM» (hora del negocio, de `payment_expires_at`);
 * sin zona, la del navegador.
 */
export function avisoDeEstado(p: Pedido, _ahora: Date, puedeReembolsar: boolean, zona: string = Intl.DateTimeFormat().resolvedOptions().timeZone): { icono: NombreIcono; titulo: string; texto: string; error: boolean } | null {
    if (p.refund_status === 'failed') {
        return { icono: 'circle-alert', titulo: 'El reembolso ha fallado.', texto: puedeReembolsar ? 'Vuelve a pedirlo con «Reintentar el reembolso».' : 'Lo reintenta el propietario o un administrador.', error: true };
    }
    if (p.refund_status === 'requested') {
        return { icono: 'rotate-ccw', titulo: 'Reembolso en curso.', texto: 'Stripe lo devuelve a su tarjeta; suele tardar de 5 a 10 días en verse en su banco.', error: false };
    }
    if (p.status === 'pending_payment') {
        const hasta = p.payment_expires_at == null || Number.isNaN(new Date(p.payment_expires_at).getTime()) ? null : horaEn(p.payment_expires_at, zona);
        return { icono: 'hourglass', titulo: hasta === null ? 'Esperando el pago.' : `Se anula sola si no se paga antes de las ${hasta}.`, texto: 'Sus plazas están guardadas mientras tanto. Si no paga, se liberan solas y el pedido queda caducado.', error: false };
    }
    if (p.status === 'expired') {
        return { icono: 'clock', titulo: 'No pagó a tiempo.', texto: 'Pasaron los 30 minutos y sus plazas se liberaron. Un pedido caducado no se reactiva.', error: false };
    }
    if (p.cancelled_by === 'customer_privacy') {
        return { icono: 'user-x', titulo: 'Cliente suprimido.', texto: 'El cliente pidió borrar sus datos: el pedido se canceló sin reembolso.', error: false };
    }
    return null;
}
